import { useEffect, useRef } from "react";

// Gaveta e diálogo de edição podem coexistir (inclusive via portal).
// Somente o modal mais recente recebe teclado e restaura foco ao fechar.
const modalStack: HTMLElement[] = [];

function canRestoreFocus(element: HTMLElement): boolean {
  if (!element.isConnected || element.closest('[inert], [hidden], [aria-hidden="true"]') || element.matches(":disabled")) return false;
  for (let current: HTMLElement | null = element; current; current = current.parentElement) {
    const style = window.getComputedStyle(current);
    if (style.display === "none" || style.visibility === "hidden" || style.visibility === "collapse") return false;
  }
  return true;
}

function restoreVisibleFocus(previouslyFocused: HTMLElement) {
  if (canRestoreFocus(previouslyFocused)) {
    previouslyFocused.focus({ preventScroll: true });
    if (document.activeElement === previouslyFocused) return;
  }
  // O editor pode ter sido aberto no desktop e fechado após virar mobile.
  // O acionador original continua no DOM, mas sua região agora está inert.
  // Usa a relação existente de aria-controls, sem acoplar o modal à sidebar.
  const controls = Array.from(document.querySelectorAll<HTMLElement>("[aria-controls]"));
  for (let region: HTMLElement | null = previouslyFocused; region; region = region.parentElement) {
    if (!region.id) continue;
    for (const control of controls) {
      if (!control.getAttribute("aria-controls")?.split(/\s+/).includes(region.id) || !canRestoreFocus(control)) continue;
      control.focus({ preventScroll: true });
      if (document.activeElement === control) return;
    }
  }
}

interface UseModalA11yOptions {
  /** True quando o modal está visível. Quando false, todos os listeners somem. */
  open: boolean;
  /** Callback disparado quando o operador aperta Escape. */
  onClose: () => void;
  /** Gavetas responsivas deixam de ser modais ao virar desktop. */
  restoreFocus?: boolean;
}

/**
 * Acessibilidade básica para modais:
 *  - Esc fecha o modal.
 *  - Tab fica preso dentro do modal (focus trap mínimo — primeiro/último
 *    elemento focável).
 *  - Foco inicial vai para o primeiro elemento focável do modal.
 *  - Ao fechar, restaura o foco no elemento que abriu o modal.
 *
 * Uso: `const containerRef = useModalA11y({ open, onClose });` e aplique
 * `ref={containerRef}` no `<div className="modal">`.
 */
export function useModalA11y<T extends HTMLElement = HTMLDivElement>({ open, onClose, restoreFocus = true }: UseModalA11yOptions) {
  const containerRef = useRef<T | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const restoreFocusRef = useRef(restoreFocus);
  restoreFocusRef.current = restoreFocus;

  useEffect(() => {
    if (!open) return;
    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused =
      typeof document !== "undefined" ? (document.activeElement as HTMLElement | null) : null;
    modalStack.push(container);

    function focusables(): HTMLElement[] {
      if (!container) return [];
      return Array.from(
        container.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
    }

    // Foco inicial: primeiro elemento focável (ou o próprio container).
    const initial = focusables();
    const target = initial[0] ?? container;
    target.focus({ preventScroll: true });

    function handleKey(event: KeyboardEvent) {
      if (modalStack.at(-1) !== container) return;
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;

      const items = focusables();
      if (items.length === 0) {
        event.preventDefault();
        container?.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("keydown", handleKey);
      const wasTopmost = modalStack.at(-1) === container;
      const index = modalStack.lastIndexOf(container);
      if (index >= 0) modalStack.splice(index, 1);
      // Estar no DOM não basta: um breakpoint pode ter escondido o acionador.
      if (wasTopmost && restoreFocusRef.current && previouslyFocused) {
        restoreVisibleFocus(previouslyFocused);
      }
    };
  }, [open]);

  return containerRef;
}
